import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import CatalogueAutocomplete from '../doctor/CatalogueAutocomplete.jsx'
import { searchLabTests } from '../../api/catalogues.js'
import { createLabStockRow } from '../../api/laboratoryStock.js'

/**
 * Flatten a DRF error payload into a readable one-line message.
 * Shared pattern with AddDrugModal / AddImagingStudyModal — recursive
 * walker so we never end up template-string-coercing an object to
 * `[object Object]`. Depth cap keeps a malformed response from looping.
 */
function formatDrfError(node, depth = 0) {
  if (depth > 5) return ''
  if (node == null) return ''
  if (typeof node === 'string') return node
  if (typeof node === 'number' || typeof node === 'boolean') return String(node)
  if (Array.isArray(node)) {
    return node
      .map((item) => formatDrfError(item, depth + 1))
      .filter(Boolean)
      .join(', ')
  }
  if (typeof node === 'object') {
    if (Object.keys(node).length === 1 && typeof node.detail === 'string') {
      return node.detail
    }
    return Object.entries(node)
      .map(([key, val]) => {
        const inner = formatDrfError(val, depth + 1)
        if (!inner) return ''
        if (key === 'non_field_errors' || key === 'detail') return inner
        return `${key}: ${inner}`
      })
      .filter(Boolean)
      .join('; ')
  }
  return ''
}

/**
 * AddLabTestModal — one-test-at-a-time catalogue entry for a lab admin.
 *
 * Mirrors the pharmacy AddDrugModal shipped in PR #66-#70:
 *   1. Pick a test from the global LabTest formulary (searchable via
 *      the existing CatalogueAutocomplete + searchLabTests).
 *   2. Enter this laboratory's specifics: price (PGK), turnaround time,
 *      offered flag, optional notes. Submit.
 *
 * The server (`LaboratoryStockViewSet.perform_create`) forces the
 * `laboratory` FK to the caller's active laboratory for non-admins, so
 * the FE doesn't need to pass a laboratory_id.
 *
 * Duplicate handling: UniqueConstraint(laboratory, lab_test) at the DB
 * layer means a POST with the same (laboratory, lab_test) fails with a
 * 400. The mutation's onError surfaces a friendly "You already offer
 * this test" so the operator can edit the existing row inline instead.
 */
export default function AddLabTestModal({ isOpen, onClose, onCreated }) {
  const qc = useQueryClient()
  const [selectedTest, setSelectedTest] = useState(null)
  const [price, setPrice] = useState('')
  const [turnaroundTime, setTurnaroundTime] = useState('')
  const [isOffered, setIsOffered] = useState(true)
  const [notes, setNotes] = useState('')
  const [error, setError] = useState('')

  const createMut = useMutation({
    mutationFn: (payload) => createLabStockRow(payload),
    onSuccess: (row) => {
      // Refresh the parent catalogue's list so the new row appears
      // immediately. Query keys match those in LabCatalogue.jsx.
      qc.invalidateQueries({ queryKey: ['lab-stock'] })
      qc.invalidateQueries({ queryKey: ['lab-stock-summary'] })
      onCreated?.(row)
      resetAndClose()
    },
    onError: (err) => {
      const detail = err?.response?.data
      const asString = formatDrfError(detail)
      const lower = asString.toLowerCase()
      if (lower.includes('unique') || lower.includes('already') || lower.includes('must make a unique set')) {
        setError('You already offer this test — edit the existing row in your catalogue instead.')
        return
      }
      setError(asString || 'Could not add test. Please check your entries and try again.')
    },
  })

  function resetAndClose() {
    setSelectedTest(null)
    setPrice('')
    setTurnaroundTime('')
    setIsOffered(true)
    setNotes('')
    setError('')
    onClose?.()
  }

  function handleSubmit(e) {
    e.preventDefault()
    setError('')
    if (!selectedTest?.id) {
      setError('Pick a test from the search first.')
      return
    }
    const priceNum = price === '' ? null : Number(price)
    if (price !== '' && (Number.isNaN(priceNum) || priceNum < 0)) {
      setError('Price must be a non-negative number (PGK).')
      return
    }
    // Server forces `laboratory` to the caller's active lab for lab
    // staff (perform_create). Admin path can pass a laboratory_id via
    // a future prop if needed — deliberately not supported here yet.
    createMut.mutate({
      lab_test: selectedTest.id,
      price: priceNum,
      turnaround_time: turnaroundTime.trim(),
      is_offered: isOffered,
      notes: notes.trim(),
    })
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-labtest-title"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[95vh] flex flex-col">
        <div className="p-4 border-b border-slate-100 shrink-0">
          <h2 id="add-labtest-title" className="text-lg font-semibold text-slate-900">
            Add lab test to catalogue
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Pick from the global test formulary, then set your price + turnaround.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto min-h-0">
          <div className="p-4 space-y-4">
            {/* Step 1 — Search + pick a global lab test */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                1. Test (search the global formulary)
              </label>
              {selectedTest ? (
                <div className="border border-emerald-200 bg-emerald-50 rounded px-3 py-2 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900">
                      {selectedTest.test_name || selectedTest.name}
                    </div>
                    <div className="text-xs text-slate-600">
                      {[
                        selectedTest.short_code,
                        selectedTest.sample_type,
                        selectedTest.category,
                      ].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedTest(null)}
                    className="text-xs text-slate-500 hover:text-slate-700 underline decoration-dotted"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <CatalogueAutocomplete
                  searchFn={searchLabTests}
                  queryKey="lab-test-formulary"
                  placeholder="Type at least 2 characters (e.g. CBC, malaria, LFT)"
                  onPick={(test) => setSelectedTest(test)}
                  renderRow={(test) => (
                    <div className="min-w-0">
                      <div className="text-sm">
                        {test.test_name || test.name}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {[
                          test.short_code,
                          test.sample_type,
                          test.category,
                        ].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                  )}
                  rowKey={(test) => test.id}
                />
              )}
            </div>

            {/* Step 2 — Laboratory specifics */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Price (PGK)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="e.g. 25.00"
                  className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-wantok-500"
                  disabled={!selectedTest || createMut.isPending}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Turnaround time
                </label>
                <input
                  type="text"
                  value={turnaroundTime}
                  onChange={(e) => setTurnaroundTime(e.target.value)}
                  placeholder="e.g. 30 min, Same day, 2-3 days"
                  className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-wantok-500"
                  disabled={!selectedTest || createMut.isPending}
                />
              </div>

              <div className="col-span-2 flex items-center pt-1">
                <input
                  id="is-offered-toggle-lab"
                  type="checkbox"
                  checked={isOffered}
                  onChange={(e) => setIsOffered(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-wantok-700 focus:ring-wantok-500"
                  disabled={!selectedTest || createMut.isPending}
                />
                <label htmlFor="is-offered-toggle-lab" className="ml-2 text-sm text-slate-700">
                  Currently offered
                </label>
              </div>

              <div className="col-span-2">
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Notes (optional, 255 char)
                </label>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. only Mon-Fri, sample must be fasting, promo until end of month"
                  maxLength={255}
                  className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-wantok-500"
                  disabled={!selectedTest || createMut.isPending}
                />
              </div>
            </div>

            {error && (
              <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1.5">
                {error}
              </div>
            )}
          </div>

          <div className="p-3 sm:p-4 flex justify-end gap-2 border-t border-slate-100 bg-white shrink-0">
            <button
              type="button"
              onClick={resetAndClose}
              disabled={createMut.isPending}
              className="text-sm px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50 text-slate-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!selectedTest || createMut.isPending}
              className="text-sm px-4 py-1.5 rounded bg-wantok-700 hover:bg-wantok-800 disabled:opacity-50 text-white font-medium"
            >
              {createMut.isPending ? 'Saving…' : 'Add to catalogue'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
